import { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';

/* Robust animated text with IntersectionObserver + fallback timer.
   Content ALWAYS becomes visible — animation is an enhancement, not a gate. */

export function SplitWords({ children, delay = 0, className = '', immediate = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0 });
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 2200);
    return () => clearTimeout(t);
  }, []);

  if (typeof children !== 'string') return children;
  const show = immediate || inView || fallback;
  const words = children.split(' ');

  return (
    <span ref={ref} className={className}>
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden">
          <motion.span
            className="inline-block"
            animate={show ? { y: 0, opacity: 1 } : { y: '100%', opacity: 0 }}
            transition={{
              duration: fallback ? 0.3 : 0.6,
              delay: show && !fallback ? delay + i * 0.035 : 0,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 && '\u00A0'}
        </span>
      ))}
    </span>
  );
}

export function FadeSlideIn({ children, delay = 0, direction = 'up', className = '', ...props }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0 });
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const show = inView || fallback;
  const offsets = { up: { y: 25 }, down: { y: -25 }, left: { x: -25 }, right: { x: 25 } };
  const d = offsets[direction] || offsets.up;

  return (
    <motion.div
      ref={ref}
      className={className}
      animate={show ? { opacity: 1, y: 0, x: 0 } : { opacity: 0, y: d.y || 0, x: d.x || 0 }}
      transition={{
        duration: fallback ? 0.3 : 0.6,
        delay: show && !fallback ? delay : 0,
        ease: [0.22, 1, 0.36, 1],
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function ScaleIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0 });
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const show = inView || fallback;

  return (
    <motion.div
      ref={ref}
      className={className}
      animate={show ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: fallback ? 0.3 : 0.5, delay: show && !fallback ? delay : 0 }}
    >
      {children}
    </motion.div>
  );
}
